import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WagmiStore, WalletAccount } from '../models/auth.models';

export interface WalletNetworkState {
  chainId: string;
  networkId: number | string;
  walletProvider: string;
  unsupportedChain?: boolean;
  connected?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class WalletStateService {
  // Only persisted preference is the user's regulated/custodial mode choice.
  // Wallet address, chainId, connector, and connection status are always read
  // from live Wagmi state via AppkitService — never rehydrated from storage.
  private readonly regulatedModeStorageKey = 'rw.regulatedMode';
  private storedRegulatedMode = this.readPersistedRegulatedMode();

  readonly wallet$ = new BehaviorSubject<WalletAccount | null>(null);
  readonly network$ = new BehaviorSubject<WalletNetworkState | null>(null);
  readonly regulated$ = new BehaviorSubject<boolean>(this.storedRegulatedMode ?? true);
  readonly wagmiStore$ = new BehaviorSubject<WagmiStore | null>(null);
  readonly wagmiConnected$ = new BehaviorSubject<boolean>(false);
  readonly recentConnectorId$ = new BehaviorSubject<string | null>(null);
  readonly requiresExplicitWalletReconnect$ = new BehaviorSubject<boolean>(false);

  setWallet(wallet: WalletAccount | null): void {
    this.wallet$.next(wallet);

    if (!wallet) {
      this.network$.next(null);
      this.syncDerivedRegulatedState();
      return;
    }

    this.network$.next({
      chainId: wallet.chainId,
      networkId: wallet.networkId,
      walletProvider: wallet.walletProvider,
      connected: Boolean(wallet.walletAddress)
    });
    this.syncDerivedRegulatedState();
  }

  getWallet(): WalletAccount | null {
    return this.wallet$.value;
  }

  setNetwork(network: WalletNetworkState | null): void {
    this.network$.next(network);
  }

  getNetwork(): WalletNetworkState | null {
    return this.network$.value;
  }

  setRegulated(value: boolean): void {
    this.storedRegulatedMode = value;
    this.writePersistedRegulatedMode(value);
    this.regulated$.next(value);
  }

  getRegulated(): boolean {
    if (this.storedRegulatedMode !== null) {
      return this.storedRegulatedMode;
    }

    if (this.regulated$.value === false) {
      return false;
    }

    return this.hasActiveNonCustodialWallet() ? false : true;
  }

  setWagmiStore(store: WagmiStore | null): void {
    this.wagmiStore$.next(store);
    this.syncDerivedRegulatedState();
  }

  getWagmiStore(): WagmiStore | null {
    return this.wagmiStore$.value;
  }

  setWagmiConnected(value: boolean): void {
    this.wagmiConnected$.next(value);
    this.syncDerivedRegulatedState();
  }

  getWagmiConnected(): boolean {
    return this.wagmiConnected$.value;
  }

  setRecentConnectorId(value: string | null): void {
    this.recentConnectorId$.next(value);
    this.syncDerivedRegulatedState();
  }

  getRecentConnectorId(): string | null {
    return this.recentConnectorId$.value;
  }

  requireExplicitWalletReconnect(): void {
    this.requiresExplicitWalletReconnect$.next(true);
  }

  clearExplicitWalletReconnectRequirement(): void {
    this.requiresExplicitWalletReconnect$.next(false);
  }

  getRequiresExplicitWalletReconnect(): boolean {
    return this.requiresExplicitWalletReconnect$.value;
  }

  clearStoredRegulatedMode(): void {
    this.storedRegulatedMode = null;
    this.writePersistedRegulatedMode(null);
    this.regulated$.next(this.hasActiveNonCustodialWallet() ? false : true);
  }

  clearWalletState(): void {
    this.wallet$.next(null);
    this.network$.next(null);
    this.wagmiStore$.next(null);
    this.wagmiConnected$.next(false);
    this.recentConnectorId$.next(null);
    this.regulated$.next(this.storedRegulatedMode ?? true);
  }

  private readPersistedRegulatedMode(): boolean | null {
    const storedValue = this.readPersistedValue(this.regulatedModeStorageKey);
    const parsedValue = this.parseStoredValue<boolean | string | number>(storedValue);

    if (typeof parsedValue === 'boolean') {
      return parsedValue;
    }

    if (typeof parsedValue === 'number') {
      return parsedValue === 1;
    }

    if (typeof parsedValue === 'string') {
      const normalized = parsedValue.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') {
        return true;
      }

      if (normalized === 'false' || normalized === '0') {
        return false;
      }
    }

    return null;
  }

  private readPersistedValue(key: string): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private parseStoredValue<T>(value: string | null): T | null {
    if (value === null || value === undefined || value === 'null' || value === 'undefined') {
      return null;
    }

    try {
      return JSON.parse(value) as T;
    } catch {
      return value as T;
    }
  }

  private hasActiveNonCustodialWallet(): boolean {
    const wallet = this.wallet$.value;
    const provider = wallet?.walletProvider || this.normalizeWalletProvider(this.recentConnectorId$.value);
    if (wallet?.walletAddress && this.isSelfCustodyProvider(provider)) {
      return true;
    }

    if (this.wagmiConnected$.value === true) {
      return true;
    }

    const storeAccount = this.wagmiStore$.value?.state?.data?.account;
    return typeof storeAccount === 'string' && storeAccount.length > 0;
  }

  private normalizeWalletProvider(connectorId: string | null): string {
    if (connectorId === 'io.metamask') {
      return 'metamask';
    }

    if (connectorId === 'walletConnect') {
      return 'walletconnect';
    }

    return connectorId ?? '';
  }

  private isSelfCustodyProvider(provider: string | null | undefined): boolean {
    return provider === 'metamask' || provider === 'walletconnect' || provider === 'io.metamask' || provider === 'walletConnect';
  }

  private syncDerivedRegulatedState(): void {
    if (this.storedRegulatedMode === true) {
      this.regulated$.next(true);
      return;
    }

    if (this.storedRegulatedMode === false) {
      this.regulated$.next(false);
      return;
    }

    this.regulated$.next(this.hasActiveNonCustodialWallet() ? false : true);
  }

  private writePersistedRegulatedMode(value: boolean | null): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (value === null) {
        window.localStorage.removeItem(this.regulatedModeStorageKey);
        window.sessionStorage.removeItem(this.regulatedModeStorageKey);
        return;
      }

      const serializedValue = JSON.stringify(value);
      window.localStorage.setItem(this.regulatedModeStorageKey, serializedValue);
      window.sessionStorage.setItem(this.regulatedModeStorageKey, serializedValue);
    } catch {
      // Ignore browser storage failures and keep the in-memory state usable.
    }
  }
}
